import { computed, shallowRef } from "vue"

import type { StickerLabelGroup } from "@/components/print/StickerPreview.vue"
import { errorMessage } from "@/lib/errors"
import { postJson } from "@/lib/fetch"
import { formatsFor } from "@/lib/printing"
import { generateStickersPath, printStickersPath } from "@/routes"
import type { PrintFormat } from "@/types/printing"

export function useStickerGeneration(allFormats: PrintFormat[]) {
  const formats = formatsFor(allFormats, "unassigned")
  const defaultFormat = formats.find((format) => format.key === "standard") ?? formats[0]
  const selectedStyle = shallowRef(defaultFormat?.key ?? "")
  const count = shallowRef(defaultFormat?.min_count ?? 1)
  const labels = shallowRef<StickerLabelGroup[] | null>(null)
  const generating = shallowRef(false)
  const printStatus = shallowRef<"idle" | "printing" | "printed" | "error">("idle")
  const printMessage = shallowRef("")

  const selectedFormat = computed(
    () => formats.find((format) => format.key === selectedStyle.value) ?? formats[0],
  )
  const selectedMediaLabel = computed(() => selectedFormat.value?.media ?? "")
  const allCodes = computed(() => labels.value?.flatMap((label) => label.codes) ?? [])

  function selectFormat(style: string) {
    const format = formats.find((candidate) => candidate.key === style)
    if (!format) return

    selectedStyle.value = format.key
    count.value = format.min_count
    resetLabels()
  }

  function setCount(value: number) {
    count.value = value
  }

  function resetLabels() {
    labels.value = null
    printStatus.value = "idle"
    printMessage.value = ""
  }

  async function generate() {
    generating.value = true
    resetLabels()

    try {
      const response = await postJson(generateStickersPath(), {
        style: selectedStyle.value,
        count: count.value,
      })
      const data = await response.json()
      labels.value = data.labels
    } catch (error) {
      printStatus.value = "error"
      printMessage.value = errorMessage(error, "Generation failed")
    } finally {
      generating.value = false
    }
  }

  async function printAll() {
    if (printStatus.value === "printing") return
    printStatus.value = "printing"
    printMessage.value = ""

    try {
      const response = await postJson(printStickersPath(), {
        codes: allCodes.value,
        style: selectedStyle.value,
      })
      const data = await response.json()
      const printed = data.results.filter(
        (result: { status: string }) => result.status === "printed",
      ).length
      const errors = data.results.filter(
        (result: { status: string }) => result.status === "error",
      ).length

      printStatus.value = errors > 0 ? "error" : "printed"
      printMessage.value = `${printed} labels printed${errors > 0 ? `, ${errors} failed` : "!"}`
    } catch (error) {
      printStatus.value = "error"
      printMessage.value = errorMessage(error, "Print failed")
    }
  }

  async function printLabel(codes: string[]) {
    try {
      await postJson(printStickersPath(), { codes, style: selectedStyle.value })
    } catch (error) {
      printStatus.value = "error"
      printMessage.value = errorMessage(error, "Print failed")
    }
  }

  return {
    formats,
    selectedStyle,
    count,
    labels,
    generating,
    printStatus,
    printMessage,
    selectedFormat,
    selectedMediaLabel,
    allCodes,
    selectFormat,
    setCount,
    resetLabels,
    generate,
    printAll,
    printLabel,
  }
}
